// Scottish Widows Horizon (BM 1-5) FE-only override.
//
// Across the BM dashboards, Scottish Widows Horizon's per-CBAM-sector allocation
// must come from FE 100% (ignore the manual bm_holding_sector_mappings /
// bm_allocations rows). The Horizon funds are fund-of-fund vehicles and every
// `bm_fund_house_holdings` row for a given Horizon BM carries the parent
// fund's ISIN — that ISIN is what we look up in `bm_fe_asset_breakdown`.
//
// If FE cannot be resolved for a given Horizon BM (no FE row, or any
// [needs split] / Unknown buckets in resolveFundCbam) we DROP the entire
// fund house from downstream aggregation. No manual fallback.

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  resolveFundCbam,
  type FeBreakdownRow,
  type FeMappingRow,
  type FeSplitOverride,
} from "@/components/fe/feMatching";

export const SW_HORIZON_NAME = "Scottish Widows Horizon";

interface FundHouseLike { id: string; name: string; benchmark: number; }
interface HoldingLike { fund_house_id: string; isin: string | null; }
interface MasterSectorLike { id: string; name: string; }

export interface SwHorizonOverride {
  /** Synthetic bm_allocations-shaped rows for SW Horizon houses where FE resolved cleanly. */
  replacement: { fund_house_id: string; benchmark: number; sector_name: string; weight_pct: number }[];
  /** SW Horizon house ids whose original bm_allocations rows should be stripped (replaced or dropped). */
  swHorizonHouseIds: Set<string>;
  /** SW Horizon house ids that must be excluded from means/denominators (FE unavailable / unresolved). */
  excludedHouseIds: Set<string>;
  ready: boolean;
}

export function useSwHorizonFeOverride(houses: FundHouseLike[] | undefined): SwHorizonOverride {
  const swHouses = useMemo(
    () => (houses ?? []).filter((h) => h.name === SW_HORIZON_NAME),
    [houses],
  );
  const swHouseIds = useMemo(() => swHouses.map((h) => h.id), [swHouses]);

  const { data: holdings } = useQuery({
    queryKey: ["sw-horizon-holdings-isin", swHouseIds.join(",")],
    enabled: swHouseIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bm_fund_house_holdings")
        .select("fund_house_id, isin")
        .in("fund_house_id", swHouseIds);
      if (error) throw error;
      return (data ?? []) as HoldingLike[];
    },
  });

  // Parent ISIN per SW Horizon house = the ISIN that appears on its holdings
  // (each Horizon BM stores the parent fund's ISIN on every holding row).
  const parentIsinByHouseId = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holdings ?? []) {
      const isin = (h.isin || "").toUpperCase().trim();
      if (!isin || m.has(h.fund_house_id)) continue;
      m.set(h.fund_house_id, isin);
    }
    return m;
  }, [holdings]);

  const allIsins = useMemo(
    () => Array.from(new Set(Array.from(parentIsinByHouseId.values()))),
    [parentIsinByHouseId],
  );

  const { data: feRows } = useQuery({
    queryKey: ["sw-horizon-fe-breakdown", allIsins.join(",")],
    enabled: allIsins.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bm_fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .in("isin", allIsins)
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      const latest = new Map<string, FeBreakdownRow>();
      for (const r of (data ?? []) as any[]) {
        const k = (r.isin || "").toUpperCase().trim();
        if (!k || latest.has(k)) continue;
        latest.set(k, { ...r, breakdown: r.breakdown ?? {} });
      }
      return latest;
    },
  });

  const { data: feMapping } = useQuery({
    queryKey: ["fe-sector-mapping"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_sector_mapping")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return (data ?? []) as FeMappingRow[];
    },
  });

  const { data: feOverrides } = useQuery({
    queryKey: ["fe-split-overrides"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_fund_split_overrides").select("*");
      if (error) throw error;
      return (data ?? []) as FeSplitOverride[];
    },
  });

  const { data: masterSectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_master_sectors")
        .select("id, name")
        .order("display_order");
      if (error) throw error;
      return (data ?? []) as MasterSectorLike[];
    },
  });

  return useMemo<SwHorizonOverride>(() => {
    const swHorizonHouseIds = new Set(swHouseIds);
    const excludedHouseIds = new Set<string>();
    const replacement: SwHorizonOverride["replacement"] = [];

    const ready = Boolean(
      houses && (swHouseIds.length === 0 ||
        (holdings && feRows && feMapping && feOverrides && masterSectors)),
    );
    if (!ready || swHouseIds.length === 0) {
      return { replacement, swHorizonHouseIds, excludedHouseIds, ready };
    }

    const mappingByCol = new Map((feMapping ?? []).map((m) => [m.fe_column_name, m]));
    const overridesByIsinCol = new Map<string, FeSplitOverride[]>();
    for (const o of feOverrides ?? []) {
      const k = `${o.isin}::${o.fe_column_name}`;
      if (!overridesByIsinCol.has(k)) overridesByIsinCol.set(k, []);
      overridesByIsinCol.get(k)!.push(o);
    }
    const masterSectorNames = new Set((masterSectors ?? []).map((s) => s.name));

    for (const house of swHouses) {
      const isin = parentIsinByHouseId.get(house.id);
      const fe = isin ? feRows?.get(isin) : undefined;
      if (!fe) { excludedHouseIds.add(house.id); continue; }

      const { allocation, total } = resolveFundCbam(fe, mappingByCol, overridesByIsinCol);
      const hasUnresolved = Object.keys(allocation).some(
        (k) => k.startsWith("[needs split] ") || k === "Unknown",
      );
      if (total <= 0 || hasUnresolved) { excludedHouseIds.add(house.id); continue; }

      // Map FE allocation to ba_master_sectors names; drop anything that can't
      // be mapped, then renormalise the remainder to 100%.
      const mapped: [string, number][] = Object.entries(allocation)
        .filter(([name, pct]) => Number(pct) > 0 && masterSectorNames.has(name))
        .map(([name, pct]) => [name, Number(pct)]);
      const sum = mapped.reduce((s, [, v]) => s + v, 0);
      if (sum <= 0) { excludedHouseIds.add(house.id); continue; }

      for (const [sector_name, pct] of mapped) {
        replacement.push({
          fund_house_id: house.id,
          benchmark: house.benchmark,
          sector_name,
          weight_pct: (pct / sum) * 100,
        });
      }
    }

    return { replacement, swHorizonHouseIds, excludedHouseIds, ready: true };
  }, [houses, swHouses, swHouseIds, holdings, parentIsinByHouseId, feRows, feMapping, feOverrides, masterSectors]);
}

/** Merge SW Horizon FE replacements into a bm_allocations-shaped array. */
export function applySwHorizonOverride<
  T extends { fund_house_id: string; benchmark: number; sector_name: string; weight_pct: number },
>(allocations: T[], ov: SwHorizonOverride): T[] {
  if (!ov.ready || ov.swHorizonHouseIds.size === 0) return allocations;
  const stripped = allocations.filter((a) => !ov.swHorizonHouseIds.has(a.fund_house_id));
  return [...stripped, ...(ov.replacement as unknown as T[])];
}
